import { Grid } from '@material-ui/core';
import CircularLoading from 'components/base/loading/CircularLoading';
import moment from 'moment';
import React, { Fragment, useEffect, useMemo, useState, useContext } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import { color } from 'styles/utils';
import { DashboardActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { debounce } from 'lodash';
import { isNormalLevelUserSelector } from 'store/modules/user';
import { T } from 'components/common/text';
import { useTranslation } from 'react-i18next';
import { PrivateSocketContext } from 'contexts/PrivateSocketContext';
import { useDidUpdateEffect } from 'lib/utils';
import ListEmpty from 'components/common/list/ListEmpty';
import { PageSubTitle } from 'components/base/title/PageTitle';

interface PartnersContainerProps {
  className?: string;
}

function MillingStatusContainer({ className }: PartnersContainerProps) {
  const {
    isNormalLevelUser,
    millingNotification: { list: millingList, pagingData: millingListPagingData },
    millingNotificationSuccess,
    millingNotificationPending,
    millingNotificationFailure,
  }: {
    isNormalLevelUser: boolean;
    millingNotification: { list: any; pagingData: OffsetPagingData };
    millingNotificationSuccess: boolean;
    millingNotificationPending: boolean;
    millingNotificationFailure: boolean;
  } = useShallowAppSelector(state => ({
    isNormalLevelUser: isNormalLevelUserSelector(state),
    millingNotification: state.dashboard.millingNotification.data || {},
    millingNotificationSuccess: state.dashboard.millingNotification.success,
    millingNotificationPending: state.dashboard.millingNotification.pending,
    millingNotificationFailure: state.dashboard.millingNotification.failure,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const { hasNewMillingEvents } = useContext(PrivateSocketContext);
  const { t } = useTranslation();

  const hasMoreMillingNotis = millingListPagingData?.page < millingListPagingData?.totalPage;
  const [millingNotis, setMillingNotis] = useState<any[]>([]);

  const setMillingNotiValue = useMemo(() => {
    return millingNotificationSuccess && setMillingNotis(draft => draft.concat(millingList));
  }, [!!millingNotificationSuccess]);

  // SECTION: function
  const onFetchMillingNoti = debounce(() => {
    const { page } = millingListPagingData;
    if (!hasMoreMillingNotis) return;
    DashboardActions.fetch_milling_notification_request({ page: page + 1 });
  }, 500);

  const handleRefreshList = () => {
    DashboardActions.fetch_milling_notification_request({ page: 1 });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    if (!!hasNewMillingEvents.value) {
      setMillingNotis([]);
      DashboardActions.fetch_milling_notification_request({ page: 1 });
      hasNewMillingEvents.setValue(false);
    }
  }, [hasNewMillingEvents.value]);

  useEffect(() => {
    DashboardActions.fetch_milling_notification_request({ page: 1 });
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useDidUpdateEffect(() => {
    if (!millingNotificationSuccess) return;
    setIsLoadSuccess(true);
  }, [millingNotificationSuccess]);
  useDidUpdateEffect(() => {
    if (!millingNotificationFailure) return;
    setIsLoadFailure(true);
  }, [millingNotificationFailure]);

  return (
    <StyledMillingStatus data-component-name="MillingStatus" item className={className}>
      <div className="dashboard__card_title_box">
        <h2 className="dashboard__card_title">
          <PageSubTitle title="HOME_TITLE_MILLING_STATUS" margin="0" />
        </h2>
      </div>

      <div className="dashboard__partners_content_box box-shadow-default border-radius-default">
        <div className="dashboard__partners_list_box">
          {millingNotis?.length < 1 && millingNotificationPending && (
            <div className="infinite-scroll-loading">
              <CircularLoading />
            </div>
          )}
          <InfiniteScroll
            className="dashboard__partners_scroll infinite-scroll-box"
            dataLength={millingNotis?.length}
            next={onFetchMillingNoti}
            hasMore={hasMoreMillingNotis}
            loader={
              <div className="infinite-scroll-loading">
                <CircularLoading />
              </div>
            }
            height={400}
          >
            {!!isLoadSuccess &&
              (millingNotis?.length > 0 ? (
                millingNotis.map((item, idx) => {
                  return (
                    <Fragment key={idx}>
                      <div className="custom_list_item_container">
                        <div className="custom_list_item_box">
                          <div className="custom_list_item">
                            <div className="custom_list_item_title">
                              {!!item.projectId ? item.projectId : item.msg}
                            </div>
                            <div className="custom_list_item_msg_box">
                              <div className="custom_list_item_msg">
                                {!!item.projectId ? (
                                  <span>{t(`${item.msg}`)}</span>
                                ) : (
                                  <T>MILLING_ERROR</T>
                                )}
                              </div>
                              <div className="custom_list_item_date">
                                {moment.unix(item.enrollDate).format('MMM. DD. HH:mm')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })
              ) : (
                <ListEmpty text="NOTIFICATION_LIST_EMPTY" />
              ))}
            {!!isLoadFailure && <ListEmpty text="GLOBAL_LIST_ERROR" isRefreshButton={true} />}
          </InfiniteScroll>
        </div>
      </div>
    </StyledMillingStatus>
  );
}

const StyledMillingStatus = styled(Grid)`
  .dashboard__partners_title_icon {
  }
  .dashboard__partners_order_btn_box {
    margin-left: 180px;
    font-size: 14px;
    color: #767676;
    .dashboard__partners_order_btn {
      &.active {
        color: ${color.blue};
        font-weight: 500;
      }
    }
  }
  .dashboard__partners_content_box {
    background-color: #fff;
    position: relative;
    height: 400px;
    width: 395px;
  }
  .dashboard__partners_list_filter_box {
    padding: 30px;
    padding-bottom: 0;

    > * {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
    }
    > label {
      margin-right: 15px;
    }
  }
  .dashboard__partners_list_box {
    position: relative;

    height: 400px;
    overflow-y: overlay;
  }

  .custom_list_item_container {
    display: inline-flex;
    align-items: center;
    position: relative;
    width: 100%;
    .custom_list_item_box {
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;
      padding: 5px;
      padding: 15px 30px;
      .custom_list_item {
        width: 100%;
        height: 40px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        row-gap: 10px;
        .custom_list_item_title {
          font-size: 15px;
          font-weight: 500;
          color: #141821;
          width: 100%;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .custom_list_item_msg_box {
          display: flex;

          justify-content: space-between;
          .custom_list_item_msg {
            font-size: 15px;
            font-weight: 500;
            color: #444444;
            width: calc(100% - 100px);
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          .custom_list_item_date {
            text-align: right;
            font-size: 13px;
            font-weight: 300;
            color: #858997;
            width: 100px;
          }
        }
      }
    }
  }
`;

export default React.memo(MillingStatusContainer);
